import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { trackEvent } from '@src/metrics';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import {
  useCreateVerifiedUserApplicantMutation,
  useSessionQuery,
  useRejectVerifiedUserMutation,
  useGetOpeningApplicantCountsLazyQuery,
} from '@src/graphql/generated';
import { Skeleton, FeaturedTalentCard } from '@src/containers/Feed/FeaturedTalentCard';
import { getInviteMessage } from '@src/util/getInviteMessage';
import { useAppDispatch } from '@src/redux';
import { closeModal, setModalContent } from '@src/redux/actions/modal';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { ConnectParams } from '@src/containers/Source/TalentCard/interfaces/connectParams.interface';
import { QueryParams } from '../interfaces/queryParams.interface';
import { useGetOpeningId } from '../hooks/useGetOpeningId';
import { FeedEmptyState } from '../FeedEmptyState';
import { Modal } from './Modal';
import { useGetOpeningRecommendedUsers } from './hooks/useGetOpeningRecommendedUsers';
import { InviteCandidatesHeader } from './InviteCandidatesHeader';
import { InviteCandidatesProps } from './props';
import './styles.scss';

export const InviteCandidates: FC<InviteCandidatesProps> = ({ isActionsDisabled, opening }) => {
  const dispatch = useAppDispatch();

  const openingId = useGetOpeningId();

  const { queryParams } = useQueryParams<QueryParams>();

  const excludedUserIds = useRef<string[]>([]);

  const [invitedUserIds, setInvitedUserIds] = useState<string[]>([]);

  const [createVerifiedApplicant] = useCreateVerifiedUserApplicantMutation();

  const [rejectVerifiedUser] = useRejectVerifiedUserMutation();

  const [fetchGetOpeningApplicantCounts, { updateQuery: updateApplicationCountsData }] =
    useGetOpeningApplicantCountsLazyQuery();

  const { data: sessionData } = useSessionQuery();

  const companyName = sessionData?.company.profile.name;

  const {
    isOpeningRecommendedUsersLoading,
    loadMoreRecommendedUsers,
    recommendedUsers,
    recommendedUsersPagination,
    removeVerifiedUser,
  } = useGetOpeningRecommendedUsers({
    excludedUserIds: excludedUserIds.current,
  });

  const updateApplicantsCountOnConfirm = () => {
    updateApplicationCountsData(prev => {
      return {
        ...prev,
        opening: {
          ...prev.opening,
          pendingApplicants: {
            ...prev.opening.pendingApplicants,
            pagination: {
              ...prev.opening.pendingApplicants.pagination,
              totalCount: prev.opening.pendingApplicants.pagination.totalCount + 1,
            },
          },
          recommendedUsers: {
            ...prev.opening.recommendedUsers,
            pagination: {
              ...prev.opening.recommendedUsers.pagination,
              totalCount: prev.opening.recommendedUsers.pagination.totalCount - 1,
            },
          },
        },
      };
    });
  };

  const updateApplicantsCountOnHide = () => {
    updateApplicationCountsData(prev => {
      return {
        ...prev,
        opening: {
          ...prev.opening,
          recommendedUsers: {
            ...prev.opening.recommendedUsers,
            pagination: {
              ...prev.opening.recommendedUsers.pagination,
              totalCount: prev.opening.recommendedUsers.pagination.totalCount - 1,
            },
          },
        },
      };
    });
  };

  const onConfirmTalent = async (userId: string, params?: ConnectParams): Promise<void> => {
    if (isActionsDisabled) {
      return;
    }

    excludedUserIds.current.push(userId);

    const openingId = params?.openingId;
    const openingTitle = params?.openingTitle;
    const pitch = params?.pitch;

    if (userId) {
      await createVerifiedApplicant({
        variables: {
          message: pitch || getInviteMessage(companyName, openingTitle),
          openingId: openingId,
          userId: userId,
        },
      });

      setInvitedUserIds(prevUserIds => [...prevUserIds, userId]);

      updateApplicantsCountOnConfirm();

      trackEvent({
        context: MetricContext.OPENING_VIEW,
        interaction: MetricAction.SEND_MESSAGE_TO_VERIFIED_TALENT,
        payload: {
          'Event Source': queryParams?.activeView,
          'Opening Id': openingId,
          'User Id': userId,
        },
      });
    }
  };

  const onHide = async (userId: string): Promise<void> => {
    if (isActionsDisabled) {
      return;
    }

    await rejectVerifiedUser({
      variables: {
        openingId: openingId,
        userId: userId,
      },
    });

    removeVerifiedUser(userId);

    updateApplicantsCountOnHide();
  };

  const onApplicantCardClick = (userId: string, isDirectConnectShownDefault?: boolean): void => {
    if (isActionsDisabled) {
      return;
    }

    const customPitchMessage = opening?.pitch || getInviteMessage(companyName, opening?.title);

    dispatch(
      setModalContent({
        className: 'opening-modal-wrapper',
        config: {
          withoutPadding: true,
        },
        content: (
          <Modal
            customPitchMessage={customPitchMessage}
            excludedUserIds={excludedUserIds.current || []}
            isDirectConnectShownDefault={!!isDirectConnectShownDefault}
            onCloseModal={() => dispatch(closeModal())}
            onConfirmTalent={(userId, params) => onConfirmTalent(userId, params)}
            updateApplicantsCountOnHide={updateApplicantsCountOnHide}
            userId={userId}
          />
        ),
        shouldNotCloseOnLocationReplace: true,
      }),
    );
  };

  useEffect(() => {
    if (queryParams.userId && !isActionsDisabled) {
      onApplicantCardClick(queryParams.userId);
    }
  }, [queryParams.userId, isActionsDisabled]);

  useLayoutEffect(() => {
    const checkScrollPositionAndLoadMore = () => {
      const {
        body: { scrollHeight },
      } = document;
      const { innerHeight, scrollY } = window;
      const distanceFromBottom = scrollHeight - innerHeight - scrollY;

      if (
        !isOpeningRecommendedUsersLoading &&
        recommendedUsersPagination?.hasMoreResults &&
        distanceFromBottom < 100 &&
        !isActionsDisabled
      ) {
        loadMoreRecommendedUsers();
      }
    };

    window.addEventListener('scroll', checkScrollPositionAndLoadMore);

    return () => {
      window.removeEventListener('scroll', checkScrollPositionAndLoadMore);

      loadMoreRecommendedUsers.cancel();
    };
  }, [isOpeningRecommendedUsersLoading, recommendedUsersPagination?.hasMoreResults, isActionsDisabled]);

  useEffect(() => {
    if (!openingId) {
      return;
    }

    fetchGetOpeningApplicantCounts({
      variables: {
        id: openingId,
      },
    });
  }, [openingId]);

  const isFeedEmpty = !isOpeningRecommendedUsersLoading && !recommendedUsers.length;

  return (
    <div className="opening-invite-candidates">
      <div className="opening-invite-candidates__header">
        <InviteCandidatesHeader />
      </div>

      <div
        className={classnames(
          'opening-invite-candidates__cards',
          recommendedUsers.length < 3 && 'opening-invite-candidates__cards--push-left',
        )}
      >
        {recommendedUsers.map(userData => (
          <FeaturedTalentCard
            invitedUserIds={invitedUserIds}
            isDirectConnect={!!openingId}
            key={userData.user.id}
            onCardClick={() => onApplicantCardClick(userData.user.id)}
            onConfirmTalent={() => onApplicantCardClick(userData.user.id, true)}
            onHide={() => onHide(userData.user.id)}
            openingId={openingId}
            userData={userData}
            wrapperClassName="opening-invite-candidates__card"
          />
        ))}

        {isOpeningRecommendedUsersLoading && (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        )}
      </div>
      {isFeedEmpty ? <FeedEmptyState /> : null}
    </div>
  );
};
